/**
 * The CRM's verdict on the deal's broker, as a chip (UI-05).
 *
 * `_broker_master` has shipped isBlocklisted, isPreferred and qualityScore
 * on the dossier context since the Parties page was wired to the CRM, and the
 * dossier read only the phone and the email: a deal a blocklisted broker
 * brought carried no warning anywhere. One chip, drawn from the context on
 * the dossier header (so the Overview and every tab show it) and from the
 * roster's facts on the Parties page. Nothing to say draws nothing — an
 * unflagged, unscored broker is not "clear", it is unremarked.
 */
import { ShieldAlert, Star } from 'lucide-react'

export type BrokerStatusFacts = {
  blocklisted: boolean
  preferred: boolean
  /** 0-100, null until the CRM scores the broker. */
  qualityScore: number | null
}

/** The status the Parties roster carries as facts (`deal_parties._facts`):
 *  ["Broker status", "Blocklisted; Preferred"], ["Quality score", "72 / 100"]. */
export function brokerStatusFromFacts(facts: ReadonlyArray<readonly [string, string]> | undefined): BrokerStatusFacts | null {
  if (!facts) return null
  const status = facts.find(([label]) => label === 'Broker status')?.[1] ?? ''
  const scoreText = facts.find(([label]) => label === 'Quality score')?.[1] ?? ''
  const score = Number.parseInt(scoreText, 10)
  const out = {
    blocklisted: /blocklisted/i.test(status),
    preferred: /preferred/i.test(status),
    qualityScore: Number.isFinite(score) ? score : null,
  }
  return out.blocklisted || out.preferred || out.qualityScore != null ? out : null
}

export function BrokerStatusChip({
  status,
  name,
  compact = false,
}: {
  status: BrokerStatusFacts | null | undefined
  /** The broker, for the hover text. */
  name?: string | null
  compact?: boolean
}) {
  if (!status || (!status.blocklisted && !status.preferred && status.qualityScore == null)) return null
  const who = name ? `${name} — ` : ''
  const score = status.qualityScore != null ? `score ${status.qualityScore}/100` : null
  const tone = status.blocklisted
    ? 'border-red-400/40 bg-red-500/10 text-red-200'
    : status.preferred
      ? 'border-amber-300/40 bg-amber-300/10 text-amber-200'
      : 'border-stone-600 bg-stone-800/60 text-stone-300'
  const label = status.blocklisted
    ? `Blocklisted broker${status.preferred ? ' · preferred' : ''}`
    : status.preferred
      ? 'Preferred broker'
      : 'Broker'
  const title = `${who}${[
    status.blocklisted ? 'on the CRM blocklist' : null,
    status.preferred ? 'a preferred broker' : null,
    score,
  ].filter(Boolean).join(' · ')}`
  return (
    <span
      role="status"
      data-testid="broker-status-chip"
      data-broker-blocklisted={status.blocklisted ? 'true' : undefined}
      data-broker-preferred={status.preferred ? 'true' : undefined}
      title={title}
      className={`inline-flex shrink-0 items-center gap-1 rounded border px-1.5 ${compact ? 'py-px text-[9px]' : 'py-0.5 text-[10px]'} font-medium uppercase tracking-wide ${tone}`}
    >
      {status.blocklisted ? <ShieldAlert size={compact ? 9 : 11} /> : status.preferred ? <Star size={compact ? 9 : 11} /> : null}
      {label}
      {score ? <span className="font-mono normal-case tracking-normal opacity-80">· {score}</span> : null}
    </span>
  )
}
